import { classNames } from '@dimjs/utils';
import { isUndefinedOrNull, LabelValueItem, TNoopDefine, toArray } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { FC, useRef, useState } from 'react';
import { Button } from '../button';
import { useEffectCustom } from '../hooks';
import { IphoneBottomAdaptive } from '../iphone-bottom-adaptive';
import { ModalDrawer, ModalDrawerProps } from '../modal-drawer';
import { px2rem } from '../utils';
import { Calendar, CalendarRefApi } from './calendar';
import { CalendarProps } from './types';

export type TCalendarModalChangeData =
  | {
      label: string;
      value: string;
    }
  | {
      label: string;
      value: LabelValueItem<string>[];
    };

export type CalendarDrawerProps = {
  title?: string;
  closeIcon?: boolean;
  visible?: boolean;
  className?: string;
  onClose?: TNoopDefine;
  modalDrawerProps?: Omit<ModalDrawerProps, 'className' | 'visible' | 'onClose' | 'title' | 'closeIcon'>;
  calendarProps: Omit<CalendarProps, 'onChange' | 'value'>;
  calendarHeight?: number;
  /**
   * 是否自动回调
   * true / 没有确定按钮
   */
  autoFinish?: boolean;
  onChange?: (data?: TCalendarModalChangeData) => void;
  /**
   * 赋值后则为受控更新
   */
  value?: string | string[];
};

export const CalendarDrawer: FC<CalendarDrawerProps> = (props) => {
  const height = props.calendarHeight ? px2rem(props.calendarHeight) : px2rem(900);
  const isMultiple = props.calendarProps.selectedType === 'two-select';
  const [tempValues, setTempValues] = useState<string[]>();

  useEffectCustom(() => {
    setTempValues(toArray(props.value));
  }, [props.value]);

  const onCalendarChange = hooks.useCallbackRef((value) => {
    const values = toArray<string>(value);
    setTempValues(values);
    if (props.autoFinish) {
      onSubmit(values);
    }
  });

  const onSubmit = hooks.useCallbackRef((values?: string[]) => {
    let valuesNew = toArray<string>(values);
    valuesNew = valuesNew.length === 1 ? [valuesNew[0], valuesNew[0]] : valuesNew;
    if (isMultiple) {
      props.onChange?.({
        label: valuesNew.join('~'),
        value: [
          { label: valuesNew[0], value: valuesNew[0] },
          { label: valuesNew[1], value: valuesNew[1] },
        ],
      });
    } else {
      props.onChange?.({ label: valuesNew[0], value: valuesNew[0] });
    }
  });

  const disabled = !tempValues || tempValues.length === 0;

  const closeIcon = isUndefinedOrNull(props.closeIcon) ? true : props.closeIcon;
  const calendarRef = useRef<CalendarRefApi>(null);

  return (
    <ModalDrawer
      {...props.modalDrawerProps}
      title={props.title || '日期选择'}
      closeIcon={closeIcon}
      className={classNames('v-calendar-modal', props.className)}
      visible={props.visible}
      onClose={props.onClose}
      onBeforeOpen={() => {
        calendarRef.current?.scrollToTarget();
      }}
    >
      <Calendar
        {...props.calendarProps}
        style={{ height }}
        value={tempValues}
        onChange={onCalendarChange}
        ref={calendarRef}
      />

      {!props.autoFinish && (
        <div className={`v-calendar-modal-operate`}>
          <Button type="primary" onClick={onSubmit.bind(null, tempValues)} disabled={disabled}>
            确定
          </Button>
        </div>
      )}
      <IphoneBottomAdaptive />
    </ModalDrawer>
  );
};

CalendarDrawer['domTypeName'] = 'CalendarDrawer';
